/**
 * Markt-Evidenz-Tabelle — „die Beweise hinter dem Wetter".
 *
 * Agent-Inventur 03.07.: In den Markt-Details maßen 7 Karten teils doppelt
 * (Makro + Stimmung = zweimal Angst/Gier; VIX + Kapitulation = zweimal
 * Panik-Level). User-Auftrag: „Ich will die Summe sehen, nicht tausend
 * Einzelpunkte." Jetzt: EINE Zeile je Beweis-Strang — Ampel, ein Satz
 * Klartext, die Zahl. Die alten Einzel-Karten bleiben im Repo.
 *
 * Contrarian-Färbung wie überall: Kapitulation/extreme Angst = GRÜN (Chance).
 */

import { useDataState } from '@/hooks/use-data-state';
import { fetchLatestMacroRegime } from '@/components/dashboard/MacroRegimePanel';
import { fetchLatestInternals } from '@/components/dashboard/CapitulationRadarCard';
import type { SentimentRow } from '@/components/dashboard/SentimentRegimePanel';
import type { MarketPhaseResult } from '@/lib/v51/market-phase';
import { cn } from '@/lib/utils';

interface EvidenzZeile {
  aspekt: string;
  dot: string; // Tailwind-Farbe des Punkts
  satz: string;
  zahl: string;
}

const DOT = {
  gruen: 'bg-emerald-400',
  teal: 'bg-teal-400',
  neutral: 'bg-zinc-500',
  gelb: 'bg-amber-400',
  orange: 'bg-orange-400',
  rot: 'bg-red-400',
  grau: 'bg-zinc-700',
} as const;

const PHASE_LABEL: Record<string, string> = {
  bull: 'Bulle',
  korrektur: 'Korrektur',
  bear: 'Bär',
  accumulation: 'Bodenbildung',
  neutral: 'Seitwärts',
};

interface Props {
  sentiment: SentimentRow | null;
  vix: number | null;
  phase: MarketPhaseResult | null;
  stocksSupportive: boolean | null;
  cryptoSupportive: boolean | null;
}

export function MarktEvidenzTabelle({
  sentiment,
  vix,
  phase,
  stocksSupportive,
  cryptoSupportive,
}: Props) {
  const macro = useDataState({
    queryKey: ['evidenz-macro-regime'],
    queryFn: () => fetchLatestMacroRegime(),
    ttlSeconds: 30 * 60,
    isEmpty: (r) => !r,
  });
  const internals = useDataState({
    queryKey: ['evidenz-internals'],
    queryFn: (signal) => fetchLatestInternals(signal),
    ttlSeconds: 30 * 60,
    isEmpty: (r) => !r,
  });

  const zeilen: EvidenzZeile[] = [];

  // 1 — Stimmung (contrarian: extreme Angst = Chance)
  {
    const sig = sentiment?.regime_signal ?? null;
    const fg = sentiment?.fg_index ?? null;
    zeilen.push({
      aspekt: 'Stimmung',
      dot: sig === 'bullish_contra' ? DOT.gruen : sig === 'bearish_contra' ? DOT.rot : sig ? DOT.neutral : DOT.grau,
      satz:
        sig === 'bullish_contra'
          ? 'Extreme Angst — historisch oft Boden, antizyklische Chance.'
          : sig === 'bearish_contra'
            ? 'Extreme Gier — historisch oft Top, defensiv bleiben.'
            : sig
              ? 'Keine Extremstimmung — kein Kontra-Signal.'
              : 'Noch keine Stimmungsdaten (kommen mit dem Abend-Update).',
      zahl: fg !== null ? `F&G ${Math.round(fg)}` : '–',
    });
  }

  // 2 — Marktphase (SPY)
  zeilen.push({
    aspekt: 'Marktphase',
    dot: phase
      ? phase.aggregate === 'bull'
        ? DOT.gruen
        : phase.aggregate === 'accumulation'
          ? DOT.teal
          : phase.aggregate === 'korrektur'
            ? DOT.gelb
            : phase.aggregate === 'bear'
              ? DOT.rot
              : DOT.neutral
      : DOT.grau,
    satz: phase?.summary ?? 'Noch keine Phasen-Daten.',
    zahl: phase ? (PHASE_LABEL[phase.aggregate] ?? phase.aggregate) : '–',
  });

  // 3 — Makro (FRED)
  {
    const m = macro.state.data ?? null;
    zeilen.push({
      aspekt: 'Makro',
      dot: m
        ? m.regime === 'risk_on'
          ? DOT.gruen
          : m.regime === 'neutral'
            ? DOT.neutral
            : m.regime === 'risk_off'
              ? DOT.gelb
              : DOT.rot
        : DOT.grau,
      satz: m
        ? m.regime === 'risk_on'
          ? 'Zinsen/Spreads/Konjunktur unterstützen — Rückenwind.'
          : m.regime === 'neutral'
            ? 'Makro-Bild gemischt — weder Rücken- noch Gegenwind.'
            : m.regime === 'risk_off'
              ? 'Makro bremst (Zinsen/Spreads angespannt) — Vorsicht.'
              : 'Makro-Krise — Kapitalschutz vor Rendite.'
        : 'Noch keine Makro-Daten.',
      zahl: m ? `${Math.round(m.regime_score)}/100` : '–',
    });
  }

  // 4 — Volatilität (VIX → Positionsgrößen-Empfehlung, wie VixRiskAdjuster)
  {
    let dot: string = DOT.grau;
    let satz = 'Noch keine VIX-Daten.';
    let zahl = '–';
    if (vix !== null) {
      zahl = `VIX ${vix.toFixed(0)}`;
      if (vix < 15) {
        dot = DOT.gruen;
        satz = 'Ruhiger Markt — volle Positionsgröße möglich.';
      } else if (vix < 20) {
        dot = DOT.neutral;
        satz = 'Normale Schwankung — Standard-Risiko.';
      } else if (vix < 25) {
        dot = DOT.gelb;
        satz = 'Erhöhte Schwankung — Positionsgröße ×0,75.';
      } else if (vix < 30) {
        dot = DOT.orange;
        satz = 'Hohe Schwankung — Positionsgröße ×0,5, defensiv.';
      } else {
        dot = DOT.rot;
        satz = 'Panik — Positionsgröße ×0,25, Kapitalschutz. Für Antizykliker: Kaufzonen beobachten.';
      }
    }
    zeilen.push({ aspekt: 'Volatilität', dot, satz, zahl });
  }

  // 5 — Marktbreite / Kapitulation (contrarian: Kapitulation = Kaufzone reift)
  {
    const i = internals.state.data ?? null;
    const flag = i?.capitulation_flag ?? null;
    zeilen.push({
      aspekt: 'Marktbreite',
      dot:
        flag === 'capitulation'
          ? DOT.gruen
          : flag === 'elevated'
            ? DOT.gelb
            : flag === 'none'
              ? DOT.neutral
              : DOT.grau,
      satz:
        i?.summary ??
        (flag === 'none' ? 'Marktbreite unauffällig.' : 'Noch keine Breite-Daten.'),
      zahl: i?.capitulation_score !== null && i?.capitulation_score !== undefined
        ? `${i.capitulation_score}/100`
        : '–',
    });
  }

  // 6 — Anlageklassen (Regime-Filter Aktien/Krypto)
  {
    const s = stocksSupportive;
    const c = cryptoSupportive;
    const beide = s === true && c === true;
    const keins = s === false && c === false;
    zeilen.push({
      aspekt: 'Anlageklassen',
      dot: s === null && c === null ? DOT.grau : beide ? DOT.gruen : keins ? DOT.rot : DOT.gelb,
      satz: `Aktien/ETF ${s === null ? '?' : s ? 'unterstützt' : 'bremst'} · Krypto ${c === null ? '?' : c ? 'unterstützt' : 'bremst'}.`,
      zahl: `${s === null ? '·' : s ? '✓' : '✗'} / ${c === null ? '·' : c ? '✓' : '✗'}`,
    });
  }

  return (
    <div className="rounded-md border border-zinc-800/80 bg-zinc-900/30">
      <div className="divide-y divide-zinc-800/60">
        {zeilen.map((z) => (
          <div key={z.aspekt} className="flex items-start gap-2.5 px-3 py-2">
            <span className={cn('mt-1 size-2 shrink-0 rounded-full', z.dot)} />
            <span className="w-24 shrink-0 text-xs font-medium text-zinc-200">{z.aspekt}</span>
            <span className="min-w-0 flex-1 text-[11px] leading-snug text-zinc-400">{z.satz}</span>
            <span className="shrink-0 font-mono text-[11px] text-zinc-300">{z.zahl}</span>
          </div>
        ))}
      </div>
      <p className="border-t border-zinc-800/60 px-3 py-1.5 text-[10px] text-zinc-600">
        Die Beweis-Stränge hinter dem Markt-Wetter — verdichtet aus Stimmung, Phase, Makro
        (FRED), VIX und Marktbreite. Grün bei Angst-Extremen = antizyklische Lesart.
      </p>
    </div>
  );
}
